import { cn } from "@/lib/utils";
import { img } from "@/lib/images";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";

/** Reusable inner-page hero: breadcrumb, eyebrow, H1, subtitle, optional image. */
export function InnerHero({
  eyebrow,
  title,
  subtitle,
  image,
  imageAlt,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  image?: string;
  imageAlt?: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="relative overflow-hidden bg-secondary/60 pt-24 md:pt-28">
      <div
        className="pointer-events-none absolute -right-24 -top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
        aria-hidden
      />
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className={cn("grid items-center gap-8", image ? "lg:grid-cols-2" : "")}>
          <div className={image ? "" : "max-w-3xl"}>
            {children}
            {eyebrow && <p className="eyebrow mt-2">{eyebrow}</p>}
            <h1 className="h-hero mt-2 font-extrabold text-ink text-balance">{title}</h1>
            {subtitle && (
              <p className="mt-4 max-w-2xl text-lg text-muted-foreground text-pretty md:text-xl">
                {subtitle}
              </p>
            )}
          </div>
          {image && (
            <Reveal variant="scale">
              <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-secondary shadow-[0_40px_80px_-40px_rgba(26,26,26,0.5)]">
                <div
                  className="pointer-events-none absolute -inset-4 -z-10 rounded-[2.5rem] bg-primary/20 blur-3xl"
                  aria-hidden
                />
                <img
                  src={img(image, 1200)}
                  srcSet={`${img(image, 320)} 320w, ${img(image, 480)} 480w, ${img(image, 640)} 640w, ${img(image, 800)} 800w, ${img(image, 1200)} 1200w`}
                  sizes="(min-width: 1024px) 480px, 92vw"
                  alt={imageAlt ?? title}
                  width={1200}
                  height={1500}
                  className="aspect-[4/5] w-full object-cover"
                  fetchPriority="high"
                />
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export function CheckList({ items }: { items: { label: string; detail?: string }[] }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.label} className="flex gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-4 w-4" />
          </span>
          <span>
            <span className="font-semibold text-ink">{it.label}</span>
            {it.detail && <span className="text-muted-foreground"> — {it.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ProseList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 text-muted-foreground">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span className="text-pretty">{it}</span>
        </li>
      ))}
    </ul>
  );
}
